import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Eye, Plus, Search, X } from "lucide-react";
import { toast } from "sonner";
import {
  CATEGORIE_KEYS, CATEGORIE_LABELS, TIPO_PERSONA_LABELS, SUB_LABELS,
  summarizeCategorie, emptyCategorie, isEmptyCategorie,
} from "@/lib/anagrafiche";
import AnagraficaDatiForm, { validateCfPivaObbligatori } from "@/components/anagrafiche/AnagraficaDatiForm";

/**
 * Sprint 2D-PART-B — Picker unificato per riferimenti anagrafici (supplier_id,
 * cointestatario_id). Layout: campo readonly + 3 icone (Lente / Occhio / +).
 *
 * Props:
 *  - label, helper
 *  - value: anagrafica id | null
 *  - onChange(id|null)
 *  - filterCategoria: top-level key (es. "fornitore_veicoli") per pre-popolare
 *    il filtro quando si apre il picker. null = libero.
 *  - excludeId: anagrafica da escludere (es. esclude supplier_id quando si
 *    sceglie il cointestatario)
 *  - contextCompanyId: (Sprint PRIV-SCOPE.3) id azienda del contesto operativo
 *    (vendita/veicolo/polizza). Se valorizzato, il picker:
 *      - filtra GET /anagrafiche?company_id=...
 *      - inietta company_id nel body POST della modale "Crea inline"
 *    Se null/undefined, il backend applica scope generale dell'utente.
 *  - testIdPrefix: prefisso per data-testid
 */
export default function AnagraficaPicker({
  label, helper, value, onChange,
  filterCategoria = null, excludeId = null, contextCompanyId = null,
  testIdPrefix = "anag-picker",
  required = false,
}) {
  const [openSearch, setOpenSearch] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [selected, setSelected] = useState(null);

  // Carica anagrafica selezionata per mostrare il nome
  useEffect(() => {
    let cancelled = false;
    if (!value) { setSelected(null); return; }
    api.get(`/anagrafiche/${value}`)
      .then((r) => { if (!cancelled) setSelected(r.data); })
      .catch(() => { if (!cancelled) setSelected(null); });
    return () => { cancelled = true; };
  }, [value]);

  const labelOf = (a) =>
    a?.ragione_sociale || `${a?.cognome || ""} ${a?.nome || ""}`.trim() || a?.codice || "";

  return (
    <Card className="p-3 bg-slate-50/50 border-dashed">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-xs uppercase tracking-wide font-medium text-slate-600">
          {label}{required && <span className="text-red-500"> *</span>}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex-1 px-3 py-2 rounded-md border border-slate-200 bg-white text-sm min-h-[36px] flex items-center justify-between"
             data-testid={`${testIdPrefix}-display`}>
          {selected ? (
            <>
              <span className="truncate">
                <span className="font-mono text-xs text-slate-500 mr-2">{selected.codice}</span>
                {labelOf(selected)}
              </span>
              <button
                type="button"
                onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onChange(null);
                }}
                className="text-slate-400 hover:text-rose-600 ml-2"
                title="Rimuovi"
                data-testid={`${testIdPrefix}-clear`}
              ><X className="h-4 w-4" /></button>
            </>
          ) : (
            <span className="text-slate-400 italic">— nessuna anagrafica selezionata</span>
          )}
        </div>
        <Button
          type="button" variant="outline" size="sm"
          onClick={() => setOpenSearch(true)}
          title="Cerca anagrafica esistente"
          data-testid={`${testIdPrefix}-search-btn`}
          className="shrink-0"
        ><Search className="h-4 w-4" /></Button>
        {selected && (
          <a
            href={`/anagrafiche/${selected.id}`}
            target="_blank" rel="noreferrer"
            title="Apri scheda anagrafica in nuova tab"
            data-testid={`${testIdPrefix}-view-btn`}
            className="shrink-0"
          >
            <Button type="button" variant="outline" size="sm">
              <Eye className="h-4 w-4" />
            </Button>
          </a>
        )}
        <Button
          type="button" variant="outline" size="sm"
          onClick={() => setOpenCreate(true)}
          title="Crea nuova anagrafica"
          data-testid={`${testIdPrefix}-create-btn`}
          className="shrink-0"
        ><Plus className="h-4 w-4" /></Button>
      </div>
      {helper && <div className="text-[11px] text-slate-400 mt-1 italic">{helper}</div>}

      <PickerDialog
        open={openSearch}
        onOpenChange={setOpenSearch}
        filterCategoria={filterCategoria}
        excludeId={excludeId}
        contextCompanyId={contextCompanyId}
        onSelect={(a) => { onChange(a.id); setOpenSearch(false); }}
        testIdPrefix={testIdPrefix}
      />
      <CreateDialog
        open={openCreate}
        onOpenChange={setOpenCreate}
        defaultCategoria={filterCategoria}
        contextCompanyId={contextCompanyId}
        onCreated={(a) => { onChange(a.id); setOpenCreate(false); }}
        testIdPrefix={testIdPrefix}
      />
    </Card>
  );
}

function PickerDialog({ open, onOpenChange, filterCategoria, excludeId, contextCompanyId, onSelect, testIdPrefix }) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [tipo, setTipo] = useState("__all__");
  // Sprint 4D (C1): i filtri partono SEMPRE da "Tutti", ignorando filterCategoria.
  // L'utente può applicarli manualmente. Evita di nascondere anagrafiche esistenti
  // e produrre duplicati involontari.
  const [catFilter, setCatFilter] = useState("__all__");

  useEffect(() => { if (open) { setQ(""); setTipo("__all__"); setCatFilter("__all__"); } }, [open]);

  useEffect(() => {
    if (!open) return;
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (tipo !== "__all__") params.set("tipo_persona", tipo);
    if (catFilter !== "__all__") params.set("categoria", catFilter);
    // Sprint PRIV-SCOPE.3: limita al contesto azienda corrente.
    if (contextCompanyId) params.set("company_id", contextCompanyId);
    params.set("page_size", "100");
    const t = setTimeout(() => {
      api.get(`/anagrafiche?${params.toString()}`)
        .then((r) => setItems((r.data?.items || []).filter((a) => a.id !== excludeId)))
        .catch(() => setItems([]));
    }, 250);
    return () => clearTimeout(t);
  }, [open, q, tipo, catFilter, excludeId, contextCompanyId]);

  const labelOf = (a) =>
    a.ragione_sociale || `${a.cognome || ""} ${a.nome || ""}`.trim() || "(senza nome)";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Scegli anagrafica</DialogTitle>
          <DialogDescription>
            Seleziona una anagrafica esistente. I filtri partono da "Tutti" — applicali manualmente per restringere.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)}
                       placeholder="Codice, nome, ragione, CF, P.IVA..."
                       className="pl-8" autoFocus
                       data-testid={`${testIdPrefix}-search-input`} />
              </div>
            </div>
            <Select value={tipo} onValueChange={setTipo}>
              <SelectTrigger className="w-40" data-testid={`${testIdPrefix}-filter-tipo`}><SelectValue placeholder="Tipo" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutti</SelectItem>
                {Object.entries(TIPO_PERSONA_LABELS).map(([k, v]) =>
                  <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={catFilter} onValueChange={setCatFilter}>
              <SelectTrigger className="w-56" data-testid={`${testIdPrefix}-filter-cat`}><SelectValue placeholder="Categoria" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutte</SelectItem>
                {CATEGORIE_KEYS.map((k) =>
                  <SelectItem key={k} value={k}>{CATEGORIE_LABELS[k]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="border border-slate-200 rounded-md max-h-[420px] overflow-y-auto divide-y">
            {items.length === 0 ? (
              <div className="p-6 text-center text-slate-400 italic">Nessun risultato</div>
            ) : items.map((a) => {
              const sum = summarizeCategorie(a.categorie);
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => onSelect(a)}
                  className="w-full text-left px-3 py-2 hover:bg-blue-50/50 flex items-center justify-between gap-3"
                  data-testid={`${testIdPrefix}-result-${a.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[11px] text-slate-500">{a.codice}</span>
                      <span className="font-medium truncate">{labelOf(a)}</span>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {sum.slice(0, 4).map((c, i) => (
                        <span key={i} className="text-[10px] px-1 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100">{c.label}</span>
                      ))}
                    </div>
                  </div>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 shrink-0">
                    {TIPO_PERSONA_LABELS[a.tipo_persona] || a.tipo_persona}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateDialog({ open, onOpenChange, defaultCategoria, contextCompanyId, onCreated, testIdPrefix }) {
  const emptyForm = () => ({
    tipo_persona: "privato",
    nome: "", cognome: "", ragione_sociale: "",
    codice_fiscale: "", partita_iva: "",
    telefono: "", mobile: "", email: "",
    categorie: emptyCategorie(),
  });
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [taxonomy, setTaxonomy] = useState({});
  // Sprint 4D (C2): gestione 409 duplicate CF
  const [dupModal, setDupModal] = useState({ open: false, existing: null });

  useEffect(() => {
    if (!open) return;
    const init = emptyForm();
    // Pre-popola categoria contesto se fornita.
    // Supporta sia top-level ("fornitore_veicoli") che dot-notation
    // ("fornitore_servizi_veicoli.garanzia_accessoria"): in quest'ultimo
    // caso pre-seleziona automaticamente la sotto-categoria specifica.
    if (defaultCategoria) {
      if (defaultCategoria.includes(".")) {
        const [topKey, subVal] = defaultCategoria.split(".", 2);
        init.categorie = { ...init.categorie, [topKey]: [subVal] };
      } else {
        init.categorie = { ...init.categorie, [defaultCategoria]: [] };
      }
    }
    setForm(init);
    setDupModal({ open: false, existing: null });
    api.get("/anagrafiche/_meta/categorie").then((r) => setTaxonomy(r.data || {})).catch(() => {});
    // eslint-disable-next-line
  }, [open]);

  const submit = async (force = false) => {
    const a = form;
    if (a.tipo_persona === "privato") {
      if (!a.nome.trim() || !a.cognome.trim()) {
        toast.error("Nome e Cognome obbligatori"); return;
      }
    } else if (!a.ragione_sociale.trim()) {
      toast.error("Ragione sociale obbligatoria"); return;
    }
    // Sprint 4D.2: validazione CF/PIVA obbligatori
    const fiscalErr = validateCfPivaObbligatori(a);
    if (fiscalErr) { toast.error(fiscalErr); return; }
    // Categoria obbligatoria
    if (isEmptyCategorie(a.categorie)) {
      // Se defaultCategoria presente ma nessun sub scelto, fallback a primo disponibile
      if (defaultCategoria) {
        if (defaultCategoria.includes(".")) {
          const [topKey, subVal] = defaultCategoria.split(".", 2);
          a.categorie = { ...a.categorie, [topKey]: [subVal] };
        } else if (taxonomy[defaultCategoria]?.length > 0) {
          a.categorie = { ...a.categorie, [defaultCategoria]: [taxonomy[defaultCategoria][0]] };
        } else {
          toast.error("Seleziona almeno 1 categoria"); return;
        }
      } else {
        toast.error("Seleziona almeno 1 categoria"); return;
      }
    }
    setSaving(true);
    try {
      // Pulisci stringhe vuote
      const body = { ...a };
      Object.keys(body).forEach((k) => {
        if (typeof body[k] === "string" && body[k].trim() === "") delete body[k];
      });
      // Sprint PRIV-SCOPE.3: inietta company_id del contesto (vendita/veicolo).
      if (contextCompanyId) body.company_id = contextCompanyId;
      const url = force ? "/anagrafiche?force_duplicate_cf=true" : "/anagrafiche";
      const r = await api.post(url, body);
      toast.success("Anagrafica creata");
      setDupModal({ open: false, existing: null });
      onCreated?.(r.data);
    } catch (e) {
      const status = e?.response?.status;
      const det = e?.response?.data?.detail;
      if (status === 409 && det?.error === "duplicate_codice_fiscale") {
        setDupModal({ open: true, existing: det.existing_anagrafica });
      } else {
        toast.error(typeof det === "string" ? det : (det?.message || "Errore"));
      }
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh]" data-testid={`${testIdPrefix}-create-modal`}>
        <DialogHeader>
          <DialogTitle>Nuova anagrafica</DialogTitle>
          <DialogDescription>
            {defaultCategoria
              ? `Suggerita categoria: ${CATEGORIE_LABELS[defaultCategoria] || defaultCategoria} (modificabile).`
              : "Compila i dati e seleziona almeno 1 categoria."}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[70vh] overflow-y-auto pr-1">
          <AnagraficaDatiForm
            value={form}
            onChange={setForm}
            taxonomy={taxonomy}
            mode="create"
            hideAccessSection={true}
            canManageAccess={false}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Annulla</Button>
          <Button onClick={() => submit(false)} disabled={saving} className="bg-blue-600 hover:bg-blue-700"
                  data-testid={`${testIdPrefix}-create-submit`}>
            {saving ? "Creazione..." : "Crea anagrafica"}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Sprint 4D (C2): modale conferma duplicato CF */}
      <DuplicateCfDialog
        open={dupModal.open}
        existing={dupModal.existing}
        onClose={() => setDupModal({ open: false, existing: null })}
        onForceCreate={() => submit(true)}
        saving={saving}
        testIdPrefix={testIdPrefix}
      />
    </Dialog>
  );
}

// -------- Sprint 4D (C2): modale duplicato CF --------
export function DuplicateCfDialog({ open, existing, onClose, onForceCreate, saving, testIdPrefix = "dup-cf" }) {
  if (!existing) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid={`${testIdPrefix}-duplicate-modal`}>
        <DialogHeader>
          <DialogTitle className="text-amber-700 flex items-center gap-2">
            ⚠ Codice fiscale già usato
          </DialogTitle>
          <DialogDescription>
            Questo CF è già usato per:
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="px-3 py-2 rounded-md bg-amber-50 border border-amber-200">
            <div className="font-mono text-xs text-amber-700">{existing.codice}</div>
            <div className="font-medium text-slate-900">{existing.label}</div>
            <div className="text-xs text-slate-500 mt-0.5">{existing.codice_fiscale}</div>
          </div>
          <div className="text-sm">
            Vuoi creare comunque un duplicato? (raro caso di omocodia)
          </div>
        </div>
        <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-between sm:items-center gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}
                  data-testid={`${testIdPrefix}-cancel`}>Annulla</Button>
          <div className="flex gap-2">
            <a href={`/anagrafiche/${existing.id}`} target="_blank" rel="noreferrer">
              <Button variant="outline" data-testid={`${testIdPrefix}-open-existing`}>
                <Eye className="h-4 w-4 mr-1" /> Apri esistente
              </Button>
            </a>
            <Button onClick={onForceCreate} disabled={saving}
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid={`${testIdPrefix}-force-create`}>
              {saving ? "..." : "Crea comunque"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
